import { useState } from 'react';
import { Search, Share2, Calendar, ShieldAlert, Code2, Image as ImageIcon } from 'lucide-react';
import { usePageEditor } from './usePageEditor';
import { config } from '@/lib/config';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/Card';
import { Input, Label, Textarea } from '@/components/ui/Input';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/Tabs';
import { MediaPicker } from '@/features/media/MediaPicker';
import { Button } from '@/components/ui/Button';
import { AIFieldAdornment } from '@/features/ai/AIFieldAdornment';

export function PageSettingsPanel() {
  const ed = usePageEditor();
  const [pickerOpen, setPickerOpen] = useState<'preview' | 'og' | null>(null);

  const titleLen = (ed.seo.title || ed.title || '').length;
  const descLen = (ed.seo.description || ed.advanced.description || '').length;
  const seoContext = {
    metadata: {
      pageTitle: ed.title,
      slug: ed.slug,
      contentSummary: buildContentSummary(ed.blocks),
    },
  };
  const titleOk = titleLen >= 30 && titleLen <= 65;
  const descOk = descLen >= 70 && descLen <= 160;
  const hasOgImage = Boolean(ed.openGraph.image || ed.advanced.preview_image);

  return (
    <div className="mx-auto max-w-5xl space-y-6 overflow-y-auto p-6 md:p-8">
      <Tabs defaultValue="seo">
        <TabsList className="mb-4">
          <TabsTrigger value="seo"><Search className="mr-1.5 size-3.5" /> SEO</TabsTrigger>
          <TabsTrigger value="og"><Share2 className="mr-1.5 size-3.5" /> Open Graph</TabsTrigger>
          <TabsTrigger value="schema"><Code2 className="mr-1.5 size-3.5" /> Schema.org</TabsTrigger>
          <TabsTrigger value="schedule"><Calendar className="mr-1.5 size-3.5" /> Programación</TabsTrigger>
          <TabsTrigger value="advanced"><ShieldAlert className="mr-1.5 size-3.5" /> Avanzado</TabsTrigger>
        </TabsList>

        {/* SEO */}
        <TabsContent value="seo">
          <Card>
            <CardHeader>
              <CardTitle>SEO básico</CardTitle>
              <CardDescription>Cómo aparece en buscadores. Si lo dejas vacío usamos el título y descripción de la página.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <div className="flex items-center justify-between">
                  <Label>Título SEO</Label>
                  <div className="flex items-center gap-2">
                    <AIFieldAdornment
                      value={ed.seo.title || ed.title || seoContext.metadata.contentSummary}
                      onChange={(t) => ed.setSeo({ ...ed.seo, title: t.slice(0, 80) })}
                      preset="seoTitle"
                      context={seoContext}
                      variant="ghost"
                      size="sm"
                      label="AI"
                    />
                    <span className={`text-xs ${titleOk ? 'text-green-600' : titleLen > 65 ? 'text-red-600' : 'text-neutral-400'}`}>
                      {titleLen}/65
                    </span>
                  </div>
                </div>
                <Input
                  value={ed.seo.title ?? ''}
                  onChange={(e) => ed.setSeo({ ...ed.seo, title: e.target.value })}
                  placeholder={ed.title}
                  maxLength={80}
                />
                <p className="mt-1 text-xs text-neutral-500">Recomendado entre 30-65 caracteres.</p>
              </div>
              <div>
                <div className="flex items-center justify-between">
                  <Label>Meta description</Label>
                  <div className="flex items-center gap-2">
                    <AIFieldAdornment
                      value={ed.seo.description || ed.advanced.description || seoContext.metadata.contentSummary}
                      onChange={(t) => ed.setSeo({ ...ed.seo, description: t.slice(0, 200) })}
                      preset="seoDescription"
                      context={seoContext}
                      variant="ghost"
                      size="sm"
                      label="AI"
                    />
                    <span className={`text-xs ${descOk ? 'text-green-600' : descLen > 160 ? 'text-red-600' : 'text-neutral-400'}`}>
                      {descLen}/160
                    </span>
                  </div>
                </div>
                <Textarea
                  rows={3}
                  value={ed.seo.description ?? ''}
                  onChange={(e) => ed.setSeo({ ...ed.seo, description: e.target.value })}
                  maxLength={200}
                />
                <p className="mt-1 text-xs text-neutral-500">Recomendado entre 70-160 caracteres.</p>
              </div>
              <div>
                <Label>Keywords (opcional)</Label>
                <Input
                  value={ed.seo.keywords ?? ''}
                  onChange={(e) => ed.setSeo({ ...ed.seo, keywords: e.target.value })}
                  placeholder="venta empresas, compraventa, M&A"
                />
              </div>

              <SerpPreview
                title={ed.seo.title || ed.title}
                description={ed.seo.description || ed.advanced.description || ''}
                slug={ed.slug}
              />
            </CardContent>
          </Card>
        </TabsContent>

        {/* OG */}
        <TabsContent value="og">
          <Card>
            <CardHeader>
              <CardTitle>Compartir en redes (Open Graph + Twitter)</CardTitle>
              <CardDescription>Cómo se ve cuando alguien comparte esta página en Facebook, LinkedIn, X o WhatsApp.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label>Título (OG)</Label>
                <Input
                  value={ed.openGraph.title ?? ''}
                  onChange={(e) => ed.setOpenGraph({ ...ed.openGraph, title: e.target.value })}
                  placeholder={ed.seo.title || ed.title}
                />
              </div>
              <div>
                <Label>Descripción (OG)</Label>
                <Textarea
                  rows={3}
                  value={ed.openGraph.description ?? ''}
                  onChange={(e) => ed.setOpenGraph({ ...ed.openGraph, description: e.target.value })}
                />
              </div>
              <div>
                <Label>Tipo</Label>
                <select
                  value={ed.openGraph.type ?? 'website'}
                  onChange={(e) => ed.setOpenGraph({ ...ed.openGraph, type: e.target.value as 'website' | 'article' })}
                  className="w-full rounded border border-neutral-200 px-3 py-2 text-sm"
                >
                  <option value="website">Website</option>
                  <option value="article">Artículo / blog</option>
                </select>
              </div>
              <div>
                <Label>Imagen (1200x630 ideal)</Label>
                <ImagePicker
                  value={ed.openGraph.image ?? ''}
                  onChange={(v) => ed.setOpenGraph({ ...ed.openGraph, image: v })}
                  open={pickerOpen === 'og'}
                  onOpenChange={(o) => setPickerOpen(o ? 'og' : null)}
                />
                {!hasOgImage && (
                  <p className="mt-1 text-xs text-amber-600">⚠ Sin imagen OG, las redes mostrarán una imagen genérica del sitio.</p>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* SCHEMA */}
        <TabsContent value="schema">
          <Card>
            <CardHeader>
              <CardTitle>Schema.org (JSON-LD)</CardTitle>
              <CardDescription>Marcado estructurado para Google Rich Results. Avanzado.</CardDescription>
            </CardHeader>
            <CardContent>
              <Label>JSON-LD</Label>
              <Textarea
                rows={14}
                value={ed.advanced.schema_data ? JSON.stringify(ed.advanced.schema_data, null, 2) : ''}
                onChange={(e) => {
                  const v = e.target.value;
                  if (v.trim() === '') {
                    ed.setAdvanced({ schema_data: null });
                    return;
                  }
                  try {
                    const parsed = JSON.parse(v);
                    ed.setAdvanced({ schema_data: parsed });
                  } catch {
                    // keep raw, will be invalid until parsed
                  }
                }}
                className="font-mono text-xs"
                placeholder='{"@context":"https://schema.org","@type":"Article","headline":"…"}'
              />
              <p className="mt-2 text-xs text-neutral-500">
                Acepta un objeto o array de objetos. Ej: <code>{"{ \"@context\": \"https://schema.org\", \"@type\": \"FAQPage\" }"}</code>.
                {' '}<a className="underline" href="https://schema.org" target="_blank" rel="noreferrer">Ver schemas</a>.
              </p>
            </CardContent>
          </Card>
        </TabsContent>

        {/* SCHEDULE */}
        <TabsContent value="schedule">
          <Card>
            <CardHeader>
              <CardTitle>Programar publicación</CardTitle>
              <CardDescription>La página se publicará automáticamente en la fecha y hora que indiques.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label>Fecha y hora</Label>
                <Input
                  type="datetime-local"
                  value={ed.advanced.scheduled_at ?? ''}
                  onChange={(e) => {
                    const v = e.target.value;
                    ed.setAdvanced({ scheduled_at: v || null });
                    if (v) ed.setMeta({ status: 'scheduled' });
                  }}
                />
                <p className="mt-1 text-xs text-neutral-500">
                  Al guardar con fecha futura, el estado pasa a "Programada". El cron de Laravel la publicará automáticamente.
                </p>
              </div>
              {ed.advanced.scheduled_at && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => ed.setAdvanced({ scheduled_at: null })}
                >
                  Cancelar programación
                </Button>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ADVANCED */}
        <TabsContent value="advanced">
          <Card>
            <CardHeader>
              <CardTitle>Avanzado</CardTitle>
              <CardDescription>Robots, canonical, descripción interna, etc.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label>Descripción / extracto</Label>
                <Textarea
                  rows={3}
                  value={ed.advanced.description ?? ''}
                  onChange={(e) => ed.setAdvanced({ description: e.target.value || null })}
                  placeholder="Descripción interna. Usada como fallback en SEO si no rellenas el título/descripción SEO."
                />
              </div>
              <div>
                <Label>Canonical URL</Label>
                <Input
                  value={ed.advanced.canonical_url ?? ''}
                  onChange={(e) => ed.setAdvanced({ canonical_url: e.target.value || null })}
                  placeholder="https://ejemplo.com/canonical-path"
                />
              </div>
              <div>
                <Label>Imagen de preview (admin)</Label>
                <ImagePicker
                  value={ed.advanced.preview_image ?? ''}
                  onChange={(v) => ed.setAdvanced({ preview_image: v || null })}
                  open={pickerOpen === 'preview'}
                  onOpenChange={(o) => setPickerOpen(o ? 'preview' : null)}
                />
              </div>
              <div className="grid gap-2 md:grid-cols-2">
                <label className="flex items-center gap-2 rounded border border-neutral-200 p-3">
                  <input
                    type="checkbox"
                    checked={ed.advanced.no_index}
                    onChange={(e) => ed.setAdvanced({ no_index: e.target.checked })}
                  />
                  <div>
                    <div className="text-sm font-medium">noindex</div>
                    <div className="text-xs text-neutral-500">No indexar en buscadores.</div>
                  </div>
                </label>
                <label className="flex items-center gap-2 rounded border border-neutral-200 p-3">
                  <input
                    type="checkbox"
                    checked={ed.advanced.no_follow}
                    onChange={(e) => ed.setAdvanced({ no_follow: e.target.checked })}
                  />
                  <div>
                    <div className="text-sm font-medium">nofollow</div>
                    <div className="text-xs text-neutral-500">No seguir enlaces de esta página.</div>
                  </div>
                </label>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}

function ImagePicker({
  value, onChange, open, onOpenChange,
}: {
  value: string;
  onChange: (v: string) => void;
  open: boolean;
  onOpenChange: (o: boolean) => void;
}) {
  return (
    <>
      <div className="flex items-start gap-2">
        {value ? (
          <div className="relative w-32 shrink-0 overflow-hidden rounded border border-neutral-200 bg-neutral-50">
            <img src={value} alt="" className="aspect-[1200/630] w-full object-cover" />
          </div>
        ) : (
          <button
            type="button"
            onClick={() => onOpenChange(true)}
            className="grid h-20 w-32 shrink-0 place-items-center rounded border border-dashed border-neutral-300 bg-neutral-50 text-neutral-400 hover:border-neutral-500"
          >
            <ImageIcon className="size-5" />
          </button>
        )}
        <div className="flex-1 space-y-1.5">
          <Input value={value} onChange={(e) => onChange(e.target.value)} placeholder="URL" />
          <Button type="button" size="sm" variant="outline" className="w-full" onClick={() => onOpenChange(true)}>
            Elegir de la biblioteca
          </Button>
        </div>
      </div>
      <MediaPicker open={open} onClose={() => onOpenChange(false)} onSelect={onChange} accept="image" />
    </>
  );
}

/**
 * Build a plain-text summary of the page content to give the LLM enough
 * context to suggest meta title/description. We extract obvious text fields
 * from each block's data (title, subtitle, html, items[].title, etc.) and
 * trim to ~1500 chars to stay inside token budgets.
 */
function buildContentSummary(blocks: Array<{ type: string; data: unknown }>): string {
  const out: string[] = [];
  const visit = (val: unknown, depth = 0) => {
    if (depth > 4 || out.join(' ').length > 1500) return;
    if (val == null) return;
    if (typeof val === 'string') {
      const clean = val.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
      if (clean.length > 1) out.push(clean);
      return;
    }
    if (Array.isArray(val)) {
      val.forEach((v) => visit(v, depth + 1));
      return;
    }
    if (typeof val === 'object') {
      Object.values(val).forEach((v) => visit(v, depth + 1));
    }
  };
  blocks.forEach((b) => visit(b.data));
  return out.join(' ').slice(0, 1500);
}

function SerpPreview({ title, description, slug }: { title: string; description: string; slug: string }) {
  const url = config.frontendUrl || 'https://tu-sitio.com';
  return (
    <div className="rounded-md border border-neutral-200 bg-white p-4">
      <div className="mb-1 text-xs text-neutral-500">Vista previa Google</div>
      <div className="text-xs text-green-700">{url}/{slug}</div>
      <div className="mt-0.5 text-base text-blue-700 underline-offset-2 hover:underline cursor-pointer">
        {title.slice(0, 65) || 'Título de la página'}
      </div>
      <div className="text-sm text-neutral-600">
        {(description || 'Descripción de la página que se mostrará en los resultados.').slice(0, 160)}
      </div>
    </div>
  );
}
